import { useState } from 'react'
import { Dialog, DialogPanel } from '@headlessui/react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import './navbar.styles.scss'
import useAuth from '../../hooks/useAuth'
import { signOutUser, successToast } from '../../utils/firebase/firebase.utils'
import { isAdminProfile } from '../../utils/firebase/auth.utils'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { scrollToContactSection } from '../../utils/scroll.utils'

const navigation = [
  { name: 'Home', href: '/' },
  { name: 'About', href: '/about' },
  { name: 'Gallery', href: '/gallery' },
  { name: 'Contact', href: '/#contact', hash: 'contact' },
  { name: 'Book', href: '/book', cta: true },
]

const linkClass = ({ isActive }) =>
  `site-nav__link${isActive ? ' site-nav__link--active' : ''}`

export default function Navbar1() {
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { currentUser, setCurrentUser } = useAuth()

  const signOutHandler = async () => {
    await signOutUser()
    setCurrentUser(null)
    successToast('Logout Successful..!')
    localStorage.setItem('curUser', '')
    setMobileMenuOpen(false)
    navigate('/')
  }

  const closeMobile = () => setMobileMenuOpen(false)

  const goToContact = (event, mobile = false) => {
    event.preventDefault()
    if (mobile) closeMobile()

    if (location.pathname === '/') {
      scrollToContactSection()
      return
    }

    navigate('/#contact')
  }

  const renderLink = (item, mobile = false) => {
    const className = mobile
      ? `site-nav__mobile-link${item.cta ? ' site-nav__mobile-link--cta' : ''}`
      : item.cta
        ? 'site-nav__cta'
        : linkClass

    if (item.hash === 'contact') {
      return (
        <a
          key={item.name}
          href="/#contact"
          onClick={(event) => goToContact(event, mobile)}
          className={typeof className === 'string' ? className : 'site-nav__link'}
        >
          {item.name}
        </a>
      )
    }

    if (item.cta && !mobile) {
      return (
        <Link key={item.name} to={item.href} className="site-nav__cta">
          {item.name}
        </Link>
      )
    }

    return (
      <NavLink
        key={item.name}
        to={item.href}
        end={item.href === '/'}
        onClick={mobile ? closeMobile : undefined}
        className={className}
      >
        {item.name}
      </NavLink>
    )
  }

  return (
    <header className="site-nav">
      <nav className="site-nav__bar" aria-label="Global">
        <Link to="/" className="site-nav__brand text-logo">
          Cake by Sandy
        </Link>

        <button
          type="button"
          className="site-nav__burger"
          onClick={() => setMobileMenuOpen(true)}
        >
          <span className="sr-only">Open main menu</span>
          <Bars3Icon className="h-6 w-6" aria-hidden="true" />
        </button>

        <div className="site-nav__links">
          {navigation.map((item) => renderLink(item))}
        </div>

        <div className="site-nav__auth">
          {isAdminProfile(currentUser) ? (
            <>
              <Link to="/manage" className="site-nav__ghost">
                Manage
              </Link>
              <button type="button" onClick={signOutHandler} className="site-nav__cta">
                Logout
              </button>
            </>
          ) : (
            <Link to="/login" className="site-nav__ghost">
              Login
            </Link>
          )}
        </div>
      </nav>

      <Dialog className="site-nav__dialog" open={mobileMenuOpen} onClose={setMobileMenuOpen}>
        <div className="site-nav__backdrop" aria-hidden="true" />
        <DialogPanel className="site-nav__drawer">
          <div className="site-nav__drawer-head">
            <Link to="/" className="site-nav__brand text-logo" onClick={closeMobile}>
              Cake by Sandy
            </Link>
            <button type="button" className="site-nav__close" onClick={closeMobile}>
              <span className="sr-only">Close menu</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          <div className="site-nav__drawer-body">
            {navigation.map((item) => renderLink(item, true))}

            <div className="site-nav__drawer-auth">
              {isAdminProfile(currentUser) ? (
                <>
                  <Link to="/manage" onClick={closeMobile} className="site-nav__mobile-link">
                    Manage
                  </Link>
                  <button
                    type="button"
                    onClick={signOutHandler}
                    className="site-nav__mobile-link site-nav__mobile-link--cta"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link to="/login" onClick={closeMobile} className="site-nav__mobile-link">
                  Login
                </Link>
              )}
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  )
}
